import type { SourceImage } from "@/components/Dropzone";
import {
  cn,
  estimateOutputSize,
  formatPixels,
  type ScaleOption,
  type UpscaleMode,
} from "@/lib/utils";

interface ControlsProps {
  scale: ScaleOption;
  mode: UpscaleMode;
  faceEnhance: boolean;
  source: SourceImage | null;
  busy: boolean;
  canRun: boolean;
  engine?: "idle" | "cloud" | "local";
  onScale: (scale: ScaleOption) => void;
  onMode: (mode: UpscaleMode) => void;
  onFaceEnhance: (value: boolean) => void;
  onRun: () => void;
}

const SCALES: { value: ScaleOption; hint: string }[] = [
  { value: 2, hint: "Light" },
  { value: 4, hint: "Stock" },
  { value: 8, hint: "Mean" },
];

export function Controls({
  scale,
  mode,
  faceEnhance,
  source,
  busy,
  canRun,
  engine = "idle",
  onScale,
  onMode,
  onFaceEnhance,
  onRun,
}: ControlsProps) {
  const estimate = source
    ? estimateOutputSize(source.width, source.height, scale)
    : null;

  return (
    <aside className="panel p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between">
        <p className="font-slam text-2xl uppercase leading-none">Console</p>
        <span className="stamp bg-tape">
          {engine === "cloud" ? "CLOUD" : engine === "local" ? "LOCAL" : "02 / SET"}
        </span>
      </div>

      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        Scale
      </p>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {SCALES.map((item) => {
          const active = scale === item.value;
          return (
            <button
              key={item.value}
              type="button"
              disabled={busy}
              onClick={() => onScale(item.value)}
              className={cn(
                "border-[3px] border-ink px-1 py-3 text-center transition",
                active
                  ? "bg-ink text-acid shadow-smash-sm"
                  : "bg-cream hover:bg-acid",
              )}
            >
              <span className="block font-slam text-3xl leading-none">
                {item.value}×
              </span>
              <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.14em]">
                {item.hint}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        Stock
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onMode("photo")}
          className={cn(
            "border-[3px] border-ink px-3 py-3 text-left",
            mode === "photo" ? "bg-acid" : "bg-cream hover:bg-paper",
          )}
        >
          <span className="block font-slam text-lg uppercase leading-none">
            Photo
          </span>
          <span className="mt-1 block text-xs">Skin / grain</span>
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onMode("anime")}
          className={cn(
            "border-[3px] border-ink px-3 py-3 text-left",
            mode === "anime" ? "bg-acid" : "bg-cream hover:bg-paper",
          )}
        >
          <span className="block font-slam text-lg uppercase leading-none">
            Drawn
          </span>
          <span className="mt-1 block text-xs">Ink / flat</span>
        </button>
      </div>

      <label
        className={cn(
          "mt-5 flex cursor-pointer items-start gap-3 border-[3px] border-ink bg-paper p-3",
          mode === "anime" && "opacity-40",
        )}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-tape"
          checked={mode === "photo" && faceEnhance}
          disabled={busy || mode === "anime"}
          onChange={(event) => onFaceEnhance(event.target.checked)}
        />
        <span>
          <span className="block font-slam text-base uppercase leading-none">
            Extra bite
          </span>
          <span className="mt-1 block text-xs leading-relaxed">
            Sharpen after the model. Off for drawings so lines stay hard.
          </span>
        </span>
      </label>

      <div className="mt-5 border-[3px] border-ink bg-crate p-3 text-cream">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-cream/60">
          Output plate
        </p>
        <p className="mt-1 font-slam text-2xl uppercase leading-none">
          {estimate ? formatPixels(estimate.width, estimate.height) : "NO FILE"}
        </p>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.12em] text-cream/60">
          {estimate ? `${estimate.megapixels.toFixed(2)} MP` : "Waiting on plate A"}
        </p>
      </div>

      <button
        type="button"
        disabled={!canRun || busy}
        onClick={onRun}
        className="btn-primary mt-5 w-full"
      >
        {busy ? "PRESSING…" : "SMASH IT"}
      </button>
    </aside>
  );
}
